import React, { useState, useEffect, useCallback } from 'react';
import { Button, Card, Typography, List, Alert, Spin, Collapse, message, Modal, Checkbox, Radio, Space, Upload } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import MaterialIcon from '../../../Icons/MaterialIcon';
import { ProfessionalType } from '../../../../types';
import jsPDF from 'jspdf';
import { useGetHRVItems, useGetHMAItems } from '../../../../hooks/professionals/useAnalyses';
import { generateHRVAndHMAPdf } from '../shared/pdfGenerators';

const { Paragraph } = Typography;
const { Panel } = Collapse;

// Type definitions for the questionnaire response
interface QuestionResponse {
  id: number;
  questionId: string;
  answerText: string;
}

interface QuestionnaireResponse {
  id: number;
  questionnaireId: string;
  userId: string;
  clientId: string;
  sessionId: string;
  status: string;
  startedAt: string;
  languageCode: string;
  questionResponses: QuestionResponse[];
}

// Type definitions for questionnaire structure
interface QuestionText {
  en: string;
  nl: string;
}

interface Question {
  id: string;
  type: string;
  text: QuestionText;
  options: any[];
  columns: any[];
  required: boolean;
  sortOrder: number;
}

interface Section {
  id: string;
  title: QuestionText;
  questions: Question[];
}

interface QuestionnaireStructure {
  id: string;
  title: QuestionText;
  description: QuestionText;
  sections: Section[];
  isActive: boolean;
  version: number;
}

// AI Analysis types
interface PrimarySecondaryAnalysis {
  primary_ie: string;
  secondary_ie: string[];
  narrative: string;
}

interface BiochemicalMarkers {
  direct: string[];
  indirect: string[];
}

interface RecommendedQuestionnaire {
  nr: number;
  label: string;
  ID: string;
}

interface HetaRecommendation {
  nr: number;
  heta_id: string;
  heta_json_code: string;
}

interface AIAnalysisResponse {
  "client-name": string;
  "professional-name": string;
  A1: PrimarySecondaryAnalysis;
  A2: string[];
  A3: BiochemicalMarkers;
  B1: RecommendedQuestionnaire[];
  B2: RecommendedQuestionnaire[];
  B3: HetaRecommendation[];
}

interface NewAIQuestionnaireProfessionalProps {
  // The professional user, for language preference
  user?: ProfessionalType;
  // The client whose results are being viewed - using same pattern as Client component
  client: any;
  // Flag to enable/disable resubmit analysis feature
  enableResubmitAnalysis?: boolean;
}

const NewAIQuestionnaireProfessional: React.FC<NewAIQuestionnaireProfessionalProps> = ({ 
  user, 
  client, 
  enableResubmitAnalysis = true // Default to enabled
}) => {
  const [questionnaireResponses, setQuestionnaireResponses] = useState<QuestionnaireResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showResults, setShowResults] = useState(false);
  const [questionnaireStructures, setQuestionnaireStructures] = useState<Map<string, QuestionnaireStructure>>(new Map());
  const [loadingStructure, setLoadingStructure] = useState(false);
  const [resubmittingAnalysis, setResubmittingAnalysis] = useState(false);
  
  // Modal and selection states
  const [isSubmissionModalVisible, setIsSubmissionModalVisible] = useState(false);
  const [selectedQuestionnaireIds, setSelectedQuestionnaireIds] = useState<number[]>([]);
  const [selectedPrompt, setSelectedPrompt] = useState<'Prompt_1' | 'Prompt_2' | 'Prompt_3'>('Prompt_1');
  const [uploadedFiles, setUploadedFiles] = useState<UploadFile[]>([]);
  const [selectedHRVIds, setSelectedHRVIds] = useState<string[]>([]);
  const [selectedHMAIds, setSelectedHMAIds] = useState<string[]>([]);

  // Fetch HRV and HMA items using the hooks
  const { data: hrvItems, loading: hrvLoading } = useGetHRVItems(client?.id || '');
  const { data: hmaItems, loading: hmaLoading } = useGetHMAItems(client?.id || '');

  // Log analysis data fetching for debugging
  console.log('[NewAIQuestionnaireProfessional] HRV Items fetched:', hrvItems?.length || 0);
  console.log('[NewAIQuestionnaireProfessional] HMA Items fetched:', hmaItems?.length || 0);
  console.log('[NewAIQuestionnaireProfessional] HRV loading:', hrvLoading);
  console.log('[NewAIQuestionnaireProfessional] HMA loading:', hmaLoading);

  // Auto-select latest HRV and HMA items for Prompt 2 and 3
  useEffect(() => {
    if ((selectedPrompt === 'Prompt_2' || selectedPrompt === 'Prompt_3') && isSubmissionModalVisible) {
      // Auto-select latest HRV item if available and none selected
      if (hrvItems && hrvItems.length > 0 && selectedHRVIds.length === 0) {
        // Sort by date and get the latest
        const sortedHRV = [...hrvItems].sort((a: any, b: any) => {
          const dateA = a.dateOfAnalysis ? new Date(a.dateOfAnalysis).getTime() : 0;
          const dateB = b.dateOfAnalysis ? new Date(b.dateOfAnalysis).getTime() : 0;
          return dateB - dateA;
        });
        const latestHRV = sortedHRV[0];
        if (latestHRV) {
          setSelectedHRVIds([latestHRV.id]);
          console.log('[NewAIQuestionnaireProfessional] Auto-selected latest HRV:', latestHRV.id);
        }
      }

      // Auto-select latest HMA item if available and none selected
      if (hmaItems && hmaItems.length > 0 && selectedHMAIds.length === 0) {
        // Sort by date and get the latest
        const sortedHMA = [...hmaItems].sort((a: any, b: any) => {
          const dateA = a.dateOfAnalysis ? new Date(a.dateOfAnalysis).getTime() : 0;
          const dateB = b.dateOfAnalysis ? new Date(b.dateOfAnalysis).getTime() : 0;
          return dateB - dateA;
        });
        const latestHMA = sortedHMA[0];
        if (latestHMA) {
          setSelectedHMAIds([latestHMA.id]);
          console.log('[NewAIQuestionnaireProfessional] Auto-selected latest HMA:', latestHMA.id);
        }
      }
    }
  }, [selectedPrompt, hrvItems, hmaItems, isSubmissionModalVisible, selectedHRVIds.length, selectedHMAIds.length]);

  // Determine the language from the professional's profile, default to 'en'
  const _userAny = user as any;
  const detectedLanguage = _userAny?.userAttributes?.language ?? _userAny?.language ?? 'en';
  const lang = detectedLanguage === 'nl' ? 'nl' : 'en';
  
  console.log('[NewAIQuestionnaireProfessional] Language detection:');
  console.log('- detectedLanguage:', detectedLanguage);
  console.log('- final lang:', lang);

  const loadingText = {
    en: 'Loading questionnaire responses...',
    nl: 'Vragenlijst antwoorden laden...',
  };

  const noResultsText = {
    en: 'The client has not yet completed the My Health Intake questionnaire.',
    nl: 'De cliënt heeft de My Health Intake vragenlijst nog niet voltooid.',
  };

  // Function to fetch questionnaire structures for multiple IDs
  const fetchQuestionnaireStructures = useCallback(async (questionnaireIds: string[]) => {
    const uniqueIds = Array.from(new Set(questionnaireIds)); // Get unique IDs
    if (uniqueIds.length === 0) return;

    console.log('[NewAIQuestionnaireProfessional] Fetching questionnaire structures for IDs:', uniqueIds);
    setLoadingStructure(true);

    try {
      const API_URL = process.env.REACT_APP_QUESTIONNAIRE_API;
      const API_KEY = process.env.REACT_APP_QUESTIONNAIRE_API_KEY;

      if (!API_KEY) {
        throw new Error('API_KEY is not configured');
      }

      const structurePromises = uniqueIds.map(async (id) => {
        const response = await fetch(`${API_URL}/api/v1/questionnaires/${id}`, {
          method: "GET",
          mode: "cors",
          headers: {
            'Authorization': API_KEY,
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
        });

        if (!response.ok) {
          console.error(`HTTP error! status: ${response.status} for ID ${id}`);
          return null;
        }

        const data: QuestionnaireStructure = await response.json();
        return { id, data };
      });

      const results = await Promise.all(structurePromises);

      setQuestionnaireStructures(prevStructures => {
        const newStructures = new Map(prevStructures);
        results.forEach(result => {
          if (result) {
            newStructures.set(result.id, result.data);
          }
        });
        console.log('[NewAIQuestionnaireProfessional] Questionnaire structures fetched:', newStructures);
        return newStructures;
      });

    } catch (err: any) {
      console.error('[NewAIQuestionnaireProfessional] Failed to fetch questionnaire structures:', err);
    } finally {
      setLoadingStructure(false);
    }
  }, []);

  const fetchQuestionnaireResponses = useCallback(async () => {
    if (!client?.id) {
      console.error('[NewAIQuestionnaireProfessional] Client ID is missing:', client);
      setError('Client ID is required to fetch questionnaire responses');
      return;
    }

    console.log('[NewAIQuestionnaireProfessional] Starting questionnaire fetch for client:', client.id);
    setLoading(true);
    setError(null);

    try {
      const API_URL = process.env.REACT_APP_QUESTIONNAIRE_API;
      const API_KEY = process.env.REACT_APP_QUESTIONNAIRE_API_KEY;


      // Use client ID for the API endpoint, matching Client component pattern
      const fullUrl = `${API_URL}/api/v1/questionnaire-responses/user/${client.id}`;
      console.log('[NewAIQuestionnaireProfessional] Full API URL:', fullUrl);

      console.log('[NewAIQuestionnaireProfessional] Making API call...');
      
      if (!API_KEY) {
        throw new Error('API_KEY is not configured');
      }

      const response = await fetch(`${API_URL}/api/v1/questionnaire-responses/user/${client.id}`, {
        method: "GET",
        mode: "cors",
        headers: {
          'Authorization': API_KEY,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      console.log('[NewAIQuestionnaireProfessional] API call successful!');
      console.log('- Status:', response.status);
      console.log('- Status Text:', response.statusText);
      console.log('- Response Data:', data);
      console.log('- Data type:', typeof data);
      console.log('- Data length (if array):', Array.isArray(data) ? data.length : 'Not an array');

      if (Array.isArray(data)) {
        data.forEach((item, index) => {
          console.log(`[NewAIQuestionnaireProfessional] Response item ${index}:`, item);
        });
      }

      setQuestionnaireResponses(data);
      if (data && data.length > 0) {
        setShowResults(true); // Auto-show results if available
        
        // Fetch questionnaire structures for all unique IDs
        const questionnaireIds = data.map((res: QuestionnaireResponse) => res.questionnaireId).filter(Boolean);
        await fetchQuestionnaireStructures(questionnaireIds);
      }
      
      console.log('[NewAIQuestionnaireProfessional] State updated successfully');
    } catch (err: any) {
      console.error('[NewAIQuestionnaireProfessional] API call failed:');
      console.error('- Error object:', err);
      console.error('- Error message:', err.message);
      console.error('- Error response:', err.response);
      console.error('- Response status:', err.response?.status);
      console.error('- Response data:', err.response?.data);
      console.error('- Response headers:', err.response?.headers);
      console.error('- Request config:', err.config);
      console.error('- Request URL:', err.config?.url);
      
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch questionnaire responses';
      console.error('[NewAIQuestionnaireProfessional] Setting error message:', errorMessage);
      setError(errorMessage);
    } finally {
      console.log('[NewAIQuestionnaireProfessional] API call completed, setting loading to false');
      setLoading(false);
    }
  }, [client, fetchQuestionnaireStructures]);

  // Auto-fetch questionnaire responses when client data is available - matching Client component pattern
  useEffect(() => {
    if (client?.id) {
      console.log('[NewAIQuestionnaireProfessional] Client data available, auto-fetching questionnaire responses');
      fetchQuestionnaireResponses();
    }
  }, [client, fetchQuestionnaireResponses]);

  const handleViewResults = () => {
    console.log('[NewAIQuestionnaireProfessional] handleViewResults called - toggling visibility');
    console.log('- Current showResults state:', showResults);
    setShowResults(!showResults);
  };


  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleString();
  };

  // Helper function to get question text from questionnaire structure
  const getQuestionText = useCallback((questionId: string, structure: QuestionnaireStructure | undefined, language: string = 'en'): string => {
    // Inline formatting to avoid depending on external function identity
    const formatQId = (qid: string) => qid.replace(/-/g, ' ').toUpperCase();

    if (!structure) {
      return formatQId(questionId); // Fallback to formatted ID
    }

    // Search through all sections and questions to find the matching question ID
    for (const section of structure.sections) {
      for (const question of section.questions) {
        if (question.id === questionId) {
          return question.text?.[language as keyof QuestionText] || question.text?.en || questionId;
        }
      }
    }

    // If not found, return formatted question ID as fallback
    return formatQId(questionId);
  }, []);

  // Function to generate PDF from questionnaire responses
  const generateQuestionnaireDataPdf = useCallback(async (selectedResponseIds: number[]): Promise<Blob> => {
    console.log('[NewAIQuestionnaireProfessional] Generating questionnaire data PDF...');
    console.log('[NewAIQuestionnaireProfessional] Selected response IDs:', selectedResponseIds);

    try {
      // Filter responses based on selection
      const responsesToInclude = questionnaireResponses.filter(response => 
        selectedResponseIds.includes(response.id)
      );

      if (!responsesToInclude || responsesToInclude.length === 0) {
        throw new Error('No questionnaire responses selected for PDF generation');
      }

      console.log('[NewAIQuestionnaireProfessional] Including responses:', responsesToInclude.length);

      // Create PDF document
      const pdf = new jsPDF();
      const pageWidth = pdf.internal.pageSize.width;
      const margin = 20;
      let yPosition = margin;

      // Helper function to add text with word wrapping
      const addText = (text: string, x: number, y: number, maxWidth: number, fontSize: number = 12) => {
        pdf.setFontSize(fontSize);
        const lines = pdf.splitTextToSize(text, maxWidth);
        pdf.text(lines, x, y);
        return y + (lines.length * fontSize * 0.4);
      };

      // Helper function to check if new page is needed
      const checkNewPage = (requiredSpace: number = 40) => {
        if (yPosition > pdf.internal.pageSize.height - requiredSpace) {
          pdf.addPage();
          yPosition = margin;
          return true;
        }
        return false;
      };

      // Helper function to safely extract value from client data field
      const extractValue = (field: any): string => {
        if (!field) return '';
        if (typeof field === 'string') return field;
        if (field.value !== undefined) return field.value || '';
        return String(field);
      };

      // PDF Header
      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'bold');
      yPosition = addText('Comprehensive Client Health Analysis', margin, yPosition, pageWidth - 2 * margin, 20);
      yPosition += 15;

      // Basic Client Information
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      yPosition = addText('Client Information', margin, yPosition, pageWidth - 2 * margin, 16);
      yPosition += 5;

      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      yPosition = addText(`Name: ${client?.firstName || ''} ${client?.familyName || ''}`, margin, yPosition, pageWidth - 2 * margin);
      yPosition = addText(`Client ID: ${client?.id || ''}`, margin, yPosition, pageWidth - 2 * margin);
      yPosition = addText(`Email: ${client?.email || ''}`, margin, yPosition, pageWidth - 2 * margin);
      yPosition = addText(`Date Generated: ${new Date().toLocaleDateString()}`, margin, yPosition, pageWidth - 2 * margin);
      yPosition += 10;

      // Enhanced Client Data Section
      if (client) {
        console.log('[NewAIQuestionnaireProfessional] Full client data for PDF:', client);

        // Biographical Information
        checkNewPage();
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        yPosition = addText('Biographical Information', margin, yPosition, pageWidth - 2 * margin, 14);
        yPosition += 5;

        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'normal');
        
        // Extract bio data if available
        if (client.client_bios && client.client_bios.length > 0) {
          const bio = client.client_bios[0];
          yPosition = addText(`Date of Birth: ${bio.date_of_birth || 'Not provided'}`, margin, yPosition, pageWidth - 2 * margin);
          yPosition = addText(`Sex: ${extractValue(bio.sex)}`, margin, yPosition, pageWidth - 2 * margin);
          yPosition = addText(`Gender Identity: ${extractValue(bio.gender_identity)}`, margin, yPosition, pageWidth - 2 * margin);
          yPosition = addText(`Ethnicity/Race: ${extractValue(bio.ethnicity_or_race)}`, margin, yPosition, pageWidth - 2 * margin);
          yPosition = addText(`Height: ${bio.length ? bio.length + ' cm' : 'Not provided'}`, margin, yPosition, pageWidth - 2 * margin);
          yPosition = addText(`Weight: ${bio.weight ? bio.weight + ' kg' : 'Not provided'}`, margin, yPosition, pageWidth - 2 * margin);
          yPosition = addText(`Waist Circumference: ${bio.waist_circumference ? bio.waist_circumference + ' cm' : 'Not provided'}`, margin, yPosition, pageWidth - 2 * margin);
          yPosition = addText(`Vegetarian: ${bio.is_vegetarian ? 'Yes' : 'No'}`, margin, yPosition, pageWidth - 2 * margin);
        }
        yPosition += 10;

        // Contact Information
        checkNewPage();
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        yPosition = addText('Contact Information', margin, yPosition, pageWidth - 2 * margin, 14);
        yPosition += 5;

        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'normal');
        
        if (client.client_personal_contact_details && client.client_personal_contact_details.length > 0) {
          const contact = client.client_personal_contact_details[0];
          yPosition = addText(`Street: ${extractValue(contact.address_street)}`, margin, yPosition, pageWidth - 2 * margin);
          yPosition = addText(`Number: ${extractValue(contact.address_number)}`, margin, yPosition, pageWidth - 2 * margin);
          yPosition = addText(`Place: ${extractValue(contact.address_place)}`, margin, yPosition, pageWidth - 2 * margin);
          yPosition = addText(`Postal Code: ${extractValue(contact.address_postal_code)}`, margin, yPosition, pageWidth - 2 * margin);
          yPosition = addText(`Country: ${extractValue(contact.address_country)}`, margin, yPosition, pageWidth - 2 * margin);
          yPosition = addText(`Residential Area: ${extractValue(contact.address_residential_area)}`, margin, yPosition, pageWidth - 2 * margin);
          yPosition = addText(`Phone: ${contact.telephone_number || 'Not provided'}`, margin, yPosition, pageWidth - 2 * margin);
        }
        yPosition += 10;

        // Family Information
        checkNewPage();
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        yPosition = addText('Family Information', margin, yPosition, pageWidth - 2 * margin, 14);
        yPosition += 5;

        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'normal');
        
        if (client.client_family_details && client.client_family_details.length > 0) {
          const family = client.client_family_details[0];
          yPosition = addText(`Marital Status: ${extractValue(family.marital_status)}`, margin, yPosition, pageWidth - 2 * margin);
          yPosition = addText(`Living Situation: ${extractValue(family.living_situation)}`, margin, yPosition, pageWidth - 2 * margin);
          yPosition = addText(`Mother Name: ${extractValue(family.mother_name)}`, margin, yPosition, pageWidth - 2 * margin);
          yPosition = addText(`Father Name: ${extractValue(family.father_name)}`, margin, yPosition, pageWidth - 2 * margin);
          yPosition = addText(`Parents Marital Status: ${extractValue(family.parents_marital_status)}`, margin, yPosition, pageWidth - 2 * margin);
          yPosition = addText(`Number of Brothers: ${family.number_of_brothers || 0}`, margin, yPosition, pageWidth - 2 * margin);
          yPosition = addText(`Number of Sisters: ${family.number_of_sisters || 0}`, margin, yPosition, pageWidth - 2 * margin);
          yPosition = addText(`Male Children: ${family.number_of_male_children || 0}`, margin, yPosition, pageWidth - 2 * margin);
          yPosition = addText(`Female Children: ${family.number_of_female_children || 0}`, margin, yPosition, pageWidth - 2 * margin);
        }
        yPosition += 10;

        // Career and Education
        checkNewPage();
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        yPosition = addText('Career & Education', margin, yPosition, pageWidth - 2 * margin, 14);
        yPosition += 5;

        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'normal');
        
        if (client.client_career_and_education_details && client.client_career_and_education_details.length > 0) {
          const career = client.client_career_and_education_details[0];
          yPosition = addText(`Profession: ${extractValue(career.profession)}`, margin, yPosition, pageWidth - 2 * margin);
          yPosition = addText(`Occupational Status: ${extractValue(career.occupational_status)}`, margin, yPosition, pageWidth - 2 * margin);
          yPosition = addText(`Education Level: ${extractValue(career.education_level)}`, margin, yPosition, pageWidth - 2 * margin);
          yPosition = addText(`Salary Level: ${extractValue(career.salary)}`, margin, yPosition, pageWidth - 2 * margin);
          yPosition = addText(`Health Insurance: ${extractValue(career.health_insurance_status)}`, margin, yPosition, pageWidth - 2 * margin);
        }
        yPosition += 10;

        // Medical History - Allergies
        checkNewPage();
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        yPosition = addText('Medical History - Allergies', margin, yPosition, pageWidth - 2 * margin, 14);
        yPosition += 5;

        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'normal');
        
        if (client.client_allergies && client.client_allergies.length > 0) {
          client.client_allergies.forEach((allergy: any, index: number) => {
            yPosition = addText(`Allergy ${index + 1}: Option ID ${allergy.option_allergy_id}`, margin, yPosition, pageWidth - 2 * margin);
          });
        } else {
          yPosition = addText('No allergies recorded', margin, yPosition, pageWidth - 2 * margin);
        }
        yPosition += 10;

        // Medical History - Surgeries
        checkNewPage();
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        yPosition = addText('Medical History - Surgeries', margin, yPosition, pageWidth - 2 * margin, 14);
        yPosition += 5;

        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'normal');
        
        if (client.client_surgeries && client.client_surgeries.length > 0) {
          client.client_surgeries.forEach((surgery: any, index: number) => {
            checkNewPage(30);
            yPosition = addText(`Surgery ${index + 1}:`, margin, yPosition, pageWidth - 2 * margin);
            yPosition = addText(`  For What: ${extractValue(surgery.for_what)}`, margin, yPosition, pageWidth - 2 * margin);
            yPosition = addText(`  When: ${extractValue(surgery.when)}`, margin, yPosition, pageWidth - 2 * margin);
            yPosition += 5;
          });
        } else {
          yPosition = addText('No surgeries recorded', margin, yPosition, pageWidth - 2 * margin);
        }
        yPosition += 10;

        // Medications and Supplements
        checkNewPage();
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        yPosition = addText('Medications & Supplements', margin, yPosition, pageWidth - 2 * margin, 14);
        yPosition += 5;

        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'normal');
        
        if (client.client_food_supplements && client.client_food_supplements.length > 0) {
          yPosition = addText('Food Supplements:', margin, yPosition, pageWidth - 2 * margin);
          client.client_food_supplements.forEach((supplement: any, index: number) => {
            yPosition = addText(`  ${index + 1}. ${supplement.content || 'No details'}`, margin, yPosition, pageWidth - 2 * margin);
          });
        }

        if (client.client_other_medications && client.client_other_medications.length > 0) {
          yPosition = addText('Other Medications:', margin, yPosition, pageWidth - 2 * margin);
          client.client_other_medications.forEach((medication: any, index: number) => {
            yPosition = addText(`  ${index + 1}. ${medication.content || 'No details'}`, margin, yPosition, pageWidth - 2 * margin);
          });
        }
        yPosition += 10;

        // Lifestyle - Vice Frequencies
        checkNewPage();
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        yPosition = addText('Lifestyle Information', margin, yPosition, pageWidth - 2 * margin, 14);
        yPosition += 5;

        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'normal');
        
        if (client.client_vice_frequencies && client.client_vice_frequencies.length > 0) {
          client.client_vice_frequencies.forEach((vice: any) => {
            yPosition = addText(`${vice.type}: ${extractValue(vice.frequency)}`, margin, yPosition, pageWidth - 2 * margin);
          });
        }

        if (client.client_addictions && client.client_addictions.length > 0) {
          yPosition = addText('Addictions:', margin, yPosition, pageWidth - 2 * margin);
          client.client_addictions.forEach((addiction: any, index: number) => {
            yPosition = addText(`  Addiction ${index + 1}: Option ID ${addiction.option_addiction_id}`, margin, yPosition, pageWidth - 2 * margin);
          });
        }
        yPosition += 10;

        // Food Intolerances
        checkNewPage();
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        yPosition = addText('Food Intolerances', margin, yPosition, pageWidth - 2 * margin, 14);
        yPosition += 5;

        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'normal');
        
        if (client.client_food_intolerances && client.client_food_intolerances.length > 0) {
          client.client_food_intolerances.forEach((intolerance: any, index: number) => {
            yPosition = addText(`Intolerance ${index + 1}: Option ID ${intolerance.option_food_intolerance_id}`, margin, yPosition, pageWidth - 2 * margin);
          });
        } else {
          yPosition = addText('No food intolerances recorded', margin, yPosition, pageWidth - 2 * margin);
        }
        yPosition += 10;

        // Important Notes
        checkNewPage();
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        yPosition = addText('Important Notes for Professionals', margin, yPosition, pageWidth - 2 * margin, 14);
        yPosition += 5;

        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'normal');
        
        if (client.client_important_note_to_professionals && client.client_important_note_to_professionals.length > 0) {
          client.client_important_note_to_professionals.forEach((note: any, index: number) => {
            checkNewPage(40);
            yPosition = addText(`Note ${index + 1} (Year: ${note.year_of_event}):`, margin, yPosition, pageWidth - 2 * margin);
            yPosition = addText(`  ${note.note}`, margin, yPosition, pageWidth - 2 * margin);
            yPosition += 5;
          });
        } else {
          yPosition = addText('No important notes recorded', margin, yPosition, pageWidth - 2 * margin);
        }
        yPosition += 15;
      }

      // Questionnaire Responses Section
      checkNewPage();
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      yPosition = addText('Questionnaire Responses', margin, yPosition, pageWidth - 2 * margin, 16);
      yPosition += 10;

      responsesToInclude.forEach((questionnaire, index) => {
        // Check if we need a new page
        checkNewPage(50);

        // Get the structure for this questionnaire
        const questionnaireStructure = questionnaireStructures.get(questionnaire.questionnaireId);

        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        yPosition = addText(`Questionnaire ${index + 1}: ${questionnaire.questionnaireId}`, margin, yPosition, pageWidth - 2 * margin, 14);
        yPosition += 5;

        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'italic');
        yPosition = addText(`Status: ${questionnaire.status} | Started: ${new Date(questionnaire.startedAt).toLocaleDateString()}`, margin, yPosition, pageWidth - 2 * margin, 10);
        yPosition += 8;

        // Questions and Answers
        if (questionnaire.questionResponses && questionnaire.questionResponses.length > 0) {
          questionnaire.questionResponses.forEach((qr) => {
            // Check if we need a new page
            checkNewPage(60);

            pdf.setFontSize(11);
            pdf.setFont('helvetica', 'bold');
            const questionText = getQuestionText(qr.questionId, questionnaireStructure, questionnaire.languageCode);
            yPosition = addText(`Q: ${questionText}`, margin, yPosition, pageWidth - 2 * margin, 11);
            yPosition += 2;

            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'normal');
            yPosition = addText(`A: ${qr.answerText}`, margin, yPosition, pageWidth - 2 * margin, 10);
            yPosition += 8;
          });
        }

        yPosition += 5;
      });

      return new Blob([pdf.output('blob')], { type: 'application/pdf' });

    } catch (error) {
      console.error('[NewAIQuestionnaireProfessional] Error generating PDF:', error);
      throw error;
    }
  }, [client, questionnaireResponses, questionnaireStructures, getQuestionText]);

  /**
   * Wrapper function for generating HRV and HMA PDF using extracted utility
   * Provides client info to the shared PDF generator
   */
  const generateHMAAndHRVPdf = useCallback(async (
    selectedHRVData: any[],
    selectedHMAData: any[]
  ): Promise<Blob> => {
    console.log('[NewAIQuestionnaireProfessional] Generating HRV/HMA PDF...', { 
      hrvCount: selectedHRVData.length, 
      hmaCount: selectedHMAData.length 
    });

    try {
      return await generateHRVAndHMAPdf(selectedHRVData, selectedHMAData, {
        id: client?.id,
        firstName: client?.firstName,
        familyName: client?.familyName,
      });
    } catch (error) {
      console.error('[NewAIQuestionnaireProfessional] Error generating HRV/HMA PDF:', error);
      throw error;
    }
  }, [client]);

  // Function to open submission modal
  const handleOpenSubmissionModal = useCallback(() => {
    // Modify to allow opening modal even if no questionnaires, as long as they can upload documents
    if ((!questionnaireResponses || questionnaireResponses.length === 0) && !enableResubmitAnalysis) {
      message.error(lang === 'en' ? 'No questionnaire responses available for analysis' : 'Geen vragenlijst antwoorden beschikbaar voor analyse');
      return;
    }

    // Do not pre-select any questionnaires by default
    setSelectedQuestionnaireIds([]);
    // Clear HRV and HMA selections - will be auto-selected for Prompt 2 & 3
    setSelectedHRVIds([]);
    setSelectedHMAIds([]);
    setUploadedFiles([]);
    setSelectedPrompt('Prompt_1');
    setIsSubmissionModalVisible(true);
  }, [questionnaireResponses, enableResubmitAnalysis, lang]);

  // Function to handle questionnaire selection
  const handleQuestionnaireSelection = useCallback((checkedValues: number[]) => {
    setSelectedQuestionnaireIds(checkedValues);
  }, []);

  // Function to handle prompt selection
  const handlePromptSelection = useCallback((value: 'Prompt_1' | 'Prompt_2' | 'Prompt_3') => {
    setSelectedPrompt(value);
    if (value === 'Prompt_1') {
      setSelectedHRVIds([]);
      setSelectedHMAIds([]);
    }
  }, []);

  // Function to handle HRV selection
  const handleHRVSelection = useCallback((checkedValues: string[]) => {
    setSelectedHRVIds(checkedValues);
  }, []);

  // Function to handle HMA selection
  const handleHMASelection = useCallback((checkedValues: string[]) => {
    setSelectedHMAIds(checkedValues);
  }, []);

  // Function to handle modal cancel
  const handleCancelSubmission = useCallback(() => {
    setIsSubmissionModalVisible(false);
  }, []);

  // Function to proceed with analysis submission
  const handleProceedWithSubmission = useCallback(async () => {
    // Validate requirements based on prompt type
    const hasQuestionnaireData = selectedQuestionnaireIds.length > 0;
    const hasUploadedFiles = uploadedFiles.length > 0;
    const hasAnalysisData = (selectedPrompt === 'Prompt_2' || selectedPrompt === 'Prompt_3') && (selectedHRVIds.length > 0 || selectedHMAIds.length > 0);
    
    // Require at least one data source
    if (!hasQuestionnaireData && !hasUploadedFiles && !hasAnalysisData) {
      message.error(lang === 'en' 
        ? 'Please select at least one questionnaire, upload a document, or select HRV/HMA data (for Prompt 2 & 3)' 
        : 'Selecteer ten minste één vragenlijst, upload een document of selecteer HRV/HMA-gegevens (voor Prompt 2 & 3)'
      );
      return;
    }

    // Close modal and proceed with submission
    setIsSubmissionModalVisible(false);
    
    // Start the actual submission process
    if (!client?.id) {
      message.error(lang === 'en' ? 'Client ID is required for analysis resubmission' : 'Client-ID vereist voor heranalyse');
      return;
    }

    console.log('[NewAIQuestionnaireProfessional] Starting AI analysis resubmission for client:', client.id);
    console.log('[NewAIQuestionnaireProfessional] Selected questionnaire IDs:', selectedQuestionnaireIds);
    console.log('[NewAIQuestionnaireProfessional] Selected HRV IDs:', selectedHRVIds);
    console.log('[NewAIQuestionnaireProfessional] Selected HMA IDs:', selectedHMAIds);
    console.log('[NewAIQuestionnaireProfessional] Uploaded files count:', uploadedFiles.length);
    console.log('[NewAIQuestionnaireProfessional] Selected prompt:', selectedPrompt);
    setResubmittingAnalysis(true);

    try {
      const API_URL = process.env.REACT_APP_QUESTIONNAIRE_API;
      const API_KEY = process.env.REACT_APP_QUESTIONNAIRE_API_KEY;

      if (!API_KEY) {
        throw new Error('API_KEY is not configured');
      }

      // Prepare the form data
      const formData = new FormData();
      formData.append('clientId', client.id);
      formData.append('professionalName', user?.firstName || 'Professional');
      formData.append('clientName', `${client?.firstName || ''} ${client?.familyName || ''}`.trim());
      
      // Set prompt type for backend
      formData.append('promptType', selectedPrompt);
      
      // Add additional client metadata for better AI analysis
      if (client.email) {
        formData.append('clientEmail', client.email);
      }
      
      // Add client bio data if available
      if (client.client_bios && client.client_bios.length > 0) {
        const bio = client.client_bios[0];
        if (bio.date_of_birth) {
          formData.append('clientDateOfBirth', bio.date_of_birth);
        }
        if (bio.sex?.value || bio.sex) {
          formData.append('clientSex', typeof bio.sex === 'string' ? bio.sex : bio.sex.value);
        }
        if (bio.weight) {
          formData.append('clientWeight', bio.weight.toString());
        }
        if (bio.length) {
          formData.append('clientHeight', bio.length.toString());
        }
      }
      
      // Add questionnaire metadata & generate PDF if questionnaires are selected
      if (selectedQuestionnaireIds && selectedQuestionnaireIds.length > 0) {
        formData.append('questionnaireCount', selectedQuestionnaireIds.length.toString());
        const selectedResponses = questionnaireResponses.filter(response => 
          selectedQuestionnaireIds.includes(response.id)
        );
        if (selectedResponses.length > 0) {
          formData.append('primaryQuestionnaireId', selectedResponses[0].questionnaireId);
          formData.append('questionnaireLanguage', selectedResponses[0].languageCode || 'en');
        }

        // Generate and append PDF from selected questionnaire responses
        const questionnairePdf = await generateQuestionnaireDataPdf(selectedQuestionnaireIds);
        const pdfFileName = `${client.id}_comprehensive_health_analysis.pdf`;
        const pdfFile = new File([questionnairePdf], pdfFileName, { type: 'application/pdf' });
        formData.append('files', pdfFile);
      }

      // Add HRV and HMA data for Prompt 2 and 3
      if ((selectedPrompt === 'Prompt_2' || selectedPrompt === 'Prompt_3') && 
          (selectedHRVIds.length > 0 || selectedHMAIds.length > 0) && 
          (hrvItems || hmaItems)) {
        const selectedHRVData = selectedHRVIds.length > 0 && hrvItems 
          ? hrvItems.filter((item: any) => selectedHRVIds.includes(item.id))
          : [];
        
        const selectedHMAData = selectedHMAIds.length > 0 && hmaItems
          ? hmaItems.filter((item: any) => selectedHMAIds.includes(item.id))
          : [];

        console.log('[NewAIQuestionnaireProfessional] Selected HRV data for PDF:', selectedHRVData);
        console.log('[NewAIQuestionnaireProfessional] Selected HMA data for PDF:', selectedHMAData);

        // Generate and append PDF from selected HRV and HMA data
        if (selectedHRVData.length > 0 || selectedHMAData.length > 0) {
          const analysisDataPdf = await generateHMAAndHRVPdf(selectedHRVData, selectedHMAData);
          const analysisDataFileName = `${client.id}_analysis_data_hrv_hma.pdf`;
          const analysisDataFile = new File([analysisDataPdf], analysisDataFileName, { type: 'application/pdf' });
          formData.append('files', analysisDataFile);
          
          console.log('[NewAIQuestionnaireProfessional] Analysis data PDF generated and attached:', analysisDataFileName);
        }

        // Also send structured JSON data for the backend to process
        if (selectedHRVData.length > 0) {
          formData.append('hrvData', JSON.stringify(selectedHRVData));
          formData.append('hrvCount', selectedHRVData.length.toString());
          console.log('[NewAIQuestionnaireProfessional] HRV structured data attached');
        }
        
        if (selectedHMAData.length > 0) {
          formData.append('hmaData', JSON.stringify(selectedHMAData));
          formData.append('hmaCount', selectedHMAData.length.toString());
          console.log('[NewAIQuestionnaireProfessional] HMA structured data attached');
        }
      }
      

      uploadedFiles.forEach(file => {
        if (file.originFileObj) {
          formData.append('files', file.originFileObj);
        }
      });
      
      console.log('[NewAIQuestionnaireProfessional] Comprehensive form data prepared:');
      console.log('- clientId:', client.id);
      console.log('- professionalName:', user?.firstName || 'Professional');
      console.log('- clientName:', `${client?.firstName || ''} ${client?.familyName || ''}`.trim());
      console.log('- clientEmail:', client.email);
      console.log('- questionnaire count:', selectedQuestionnaireIds?.length || 0);
      console.log('- prompt type:', selectedPrompt);
      console.log('- uploaded files:', uploadedFiles.length);

      console.log('[NewAIQuestionnaireProfessional] Sending resubmit request to AI tool...');
      
      // Send to AI analysis endpoint
      const response = await fetch(`${API_URL}/api/v1/ai-tool-daisy/analyze`, {
        method: "POST",
        mode: "cors",
        headers: {
          'Authorization': API_KEY,
        },
        body: formData,
      });

      console.log('[NewAIQuestionnaireProfessional] Response received:');
      console.log('- status:', response.status);
      console.log('- statusText:', response.statusText);
      console.log('- ok:', response.ok);

      if (!response.ok) {
        // Try to get error details from response
        let errorDetails = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorBody = await response.text();
          console.log('[NewAIQuestionnaireProfessional] Error response body:', errorBody);
          errorDetails += ` - ${errorBody}`;
        } catch (e) {
          console.log('[NewAIQuestionnaireProfessional] Could not read error response body');
        }
        throw new Error(errorDetails);
      }

      const aiAnalysisData: AIAnalysisResponse = await response.json();
      console.log('[NewAIQuestionnaireProfessional] AI analysis response received:', aiAnalysisData);
      
      message.success(lang === 'en' ? 'Analysis submitted successfully!' : 'Analyse succesvol ingediend!');
      
    } catch (err: any) {
      console.error('[NewAIQuestionnaireProfessional] Failed to resubmit analysis:', err);
      const errorMessage = err.message || 'Failed to resubmit analysis';
      message.error(lang === 'en' ? errorMessage : 'Fout bij opnieuw indienen analyse');
    } finally {
      setResubmittingAnalysis(false);
      setUploadedFiles([]);
    }
  }, [client, user, lang, selectedQuestionnaireIds, uploadedFiles, selectedPrompt, questionnaireResponses, generateQuestionnaireDataPdf, generateHMAAndHRVPdf, selectedHRVIds, selectedHMAIds, hrvItems, hmaItems]);

  const renderQuestionnaireResponse = (response: QuestionnaireResponse) => {
    // Get the structure for this specific response
    const questionnaireStructure = questionnaireStructures.get(response.questionnaireId);
    
    // Create a map of questionId to answerText for easy lookup
    const answerMap = new Map(response.questionResponses.map(qr => [qr.questionId, qr.answerText]));

    return (
      <Panel
        header={
          <div>
            <strong>{questionnaireStructure?.title?.[response.languageCode as keyof QuestionText] || questionnaireStructure?.title?.en || response.questionnaireId}</strong>
            <div style={{ fontSize: '12px', color: '#666' }}>
              Status: {response.status} | Started: {formatDate(response.startedAt)} | Language: {response.languageCode} | Questions Answered: {response.questionResponses?.length || 0}
            </div>
          </div>
        }
        key={response.id}
      >
        {questionnaireStructure ? (
          <div>
            {questionnaireStructure.sections.map(section => (
              <div key={section.id} style={{ marginBottom: '24px' }}>
                <h3 style={{ marginTop: '16px', marginBottom: '12px', borderBottom: '1px solid #eee', paddingBottom: '8px', fontSize: '16px', fontWeight: 'bold' }}>
                  {section.title?.[response.languageCode as keyof QuestionText] || section.title?.en || section.id}
                </h3>
                <List
                  dataSource={section.questions.sort((a, b) => a.sortOrder - b.sortOrder)}
                  renderItem={(question) => {
                    const answer = answerMap.get(question.id);
                    return (
                      <List.Item>
                        <div style={{ width: '100%' }}>
                          <strong style={{ display: 'block', marginBottom: '4px' }}>
                            {question.text?.[response.languageCode as keyof QuestionText] || question.text?.en || question.id}
                          </strong>
                          {answer !== undefined ? (
                            <div style={{ color: '#333', backgroundColor: '#f5f5f5', padding: '8px 12px', borderRadius: '4px' }}>
                              {answer}
                            </div>
                          ) : (
                            <div style={{ color: '#999', fontStyle: 'italic' }}>
                              No answer provided.
                            </div>
                          )}
                        </div>
                      </List.Item>
                    );
                  }}
                />
              </div>
            ))}
          </div>
        ) : (
          // Fallback to original implementation if structure is not yet loaded
          <List
            dataSource={response.questionResponses}
            renderItem={(questionResponse) => (
              <List.Item>
                <div style={{ width: '100%' }}>
                  <strong>{getQuestionText(questionResponse.questionId, questionnaireStructure, response.languageCode)}:</strong>
                  <div style={{ marginTop: '4px', color: '#333', backgroundColor: '#f5f5f5', padding: '8px', borderRadius: '4px' }}>
                    {questionResponse.answerText}
                  </div>
                </div>
              </List.Item>
            )}
          />
        )}
      </Panel>
    );
  };

  const hasResults = questionnaireResponses.length > 0;

  return (
    <Card>
      {/* <Title level={5}>My Health Intake</Title> */}
      
      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          style={{ marginBottom: 16 }}
        />
      )}

      {(loading || loadingStructure) ? (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Spin size="large" />
          <div style={{ marginTop: 8 }}>
            {loading && loadingStructure 
              ? (lang === 'en' ? 'Loading responses and questionnaire details...' : 'Antwoorden en vragenlijst details laden...')
              : loading 
                ? loadingText[lang]
                : (lang === 'en' ? 'Loading questionnaire details...' : 'Vragenlijst details laden...')
            }
          </div>
        </div>
      ) : hasResults || enableResubmitAnalysis ? (
        <>
          {hasResults ? (
            <Paragraph>
              {lang === 'en' 
                ? `Found ${questionnaireResponses.length} questionnaire response(s) for this client.`
                : `${questionnaireResponses.length} vragenlijst antwoord(en) gevonden voor deze cliënt.`
              }
            </Paragraph>
          ) : (
             <Paragraph>
              {noResultsText[lang]}
            </Paragraph>
          )}
          
          {/* Resubmit Analysis Section */}
          {enableResubmitAnalysis && (
            <div style={{ marginBottom: 16, padding: '12px', backgroundColor: '#f0f2ff', borderRadius: '6px', border: '1px solid #d6e4ff' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                <div>
                  <strong style={{ color: '#1890ff' }}>
                    {lang === 'en' ? 'Analysis' : 'Analyse'}
                  </strong>
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                    {lang === 'en' 
                      ? 'Submit questionnaire data or custom documents for AI analysis' 
                      : 'Vragenlijst data of aangepaste documenten indienen voor AI-analyse'
                    }
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <Button 
                    type="primary"
                    onClick={handleOpenSubmissionModal}
                    loading={resubmittingAnalysis}
                    icon={<MaterialIcon className="material-icons-sharp" icon="psychology" />}
                    size="small"
                  >
                    {resubmittingAnalysis 
                      ? (lang === 'en' ? 'Submitting...' : 'Indienen...')
                      : (lang === 'en' ? 'Submit for Analysis' : 'Indienen voor Analyse')
                    }
                  </Button>
                </div>
              </div>
            </div>
          )}

          {hasResults && (
            <Button
              type={showResults ? "default" : "primary"}
              onClick={handleViewResults}
              style={{ marginBottom: showResults ? 16 : 0 }}
              icon={<MaterialIcon className="material-icons-sharp" icon={showResults ? "visibility_off" : "visibility"} />}
            >
              {showResults
                ? (lang === 'en' ? 'Hide Results' : 'Verberg Resultaten')
                : (lang === 'en' ? 'Show Results' : 'Toon Resultaten')
              }
            </Button>
          )}

          {showResults && hasResults && (
            <Collapse defaultActiveKey={questionnaireResponses.map(r => r.id)}>
              {questionnaireResponses.map(renderQuestionnaireResponse)}
            </Collapse>
          )}
        </>
      ) : (
        <Paragraph>
          {noResultsText[lang]}
        </Paragraph>
      )}

      {/* Submission Configuration Modal */}
      <Modal
        title={lang === 'en' ? 'Configure Analysis Submission' : 'Analyseindiening configureren'}
        open={isSubmissionModalVisible}
        onCancel={handleCancelSubmission}
        width={700}
        footer={[
          <Button key="cancel" onClick={handleCancelSubmission}>
            {lang === 'en' ? 'Cancel' : 'Annuleren'}
          </Button>,
          <Button 
            key="submit" 
            type="primary" 
            onClick={handleProceedWithSubmission}
            loading={resubmittingAnalysis}
            disabled={
              selectedQuestionnaireIds.length === 0 && 
              uploadedFiles.length === 0 && 
              (selectedPrompt === 'Prompt_1' || 
               ((selectedPrompt === 'Prompt_2' || selectedPrompt === 'Prompt_3') && selectedHRVIds.length === 0 && selectedHMAIds.length === 0))
            }
          >
            {lang === 'en' ? 'Submit' : 'Indienen'}
          </Button>,
        ]}
      >

        {/* Existing Questionnaires Section */}
        {questionnaireResponses && questionnaireResponses.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <h4 style={{ marginBottom: 12, fontWeight: 'bold' }}>
              {lang === 'en' ? 'Select Questionnaires' : 'Selecteer vragenlijsten'}
            </h4>
            <Alert
              message={lang === 'en' 
                ? 'Select which questionnaires to include in the analysis' 
                : 'Selecteer welke vragenlijsten in de analyse moeten worden opgenomen'
              }
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
            />
            <Checkbox.Group
              value={selectedQuestionnaireIds}
              onChange={handleQuestionnaireSelection}
              style={{ width: '100%' }}
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                {questionnaireResponses.map((response) => {
                  const structure = questionnaireStructures.get(response.questionnaireId);
                  const title = structure?.title[response.languageCode as keyof QuestionText] 
                    || structure?.title.en 
                    || response.questionnaireId;
                  
                  return (
                    <div 
                      key={response.id}
                      style={{ 
                        padding: '12px', 
                        border: '1px solid #d9d9d9', 
                        borderRadius: '4px',
                        backgroundColor: selectedQuestionnaireIds.includes(response.id) ? '#f0f5ff' : '#fff'
                      }}
                    >
                      <Checkbox value={response.id}>
                        <div>
                          <strong>{title}</strong>
                          <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                            Status: {response.status} | 
                            Started: {formatDate(response.startedAt)} | 
                            Questions: {response.questionResponses?.length || 0}
                          </div>
                        </div>
                      </Checkbox>
                    </div>
                  );
                })}
              </Space>
            </Checkbox.Group>
          </div>
        )}

        {/* HRV and HMA Items Section - For Prompt 2 and 3 */}
        {(selectedPrompt === 'Prompt_2' || selectedPrompt === 'Prompt_3') && (
          <div style={{ marginBottom: 24 }}>
            <h4 style={{ marginBottom: 12, fontWeight: 'bold' }}>
              {lang === 'en' ? 'Analysis Data (HRV & HMA)' : 'Analysegegevens (HRV & HMA)'}
            </h4>
            <Alert
              message={lang === 'en' 
                ? 'Latest HRV and HMA items are automatically included' 
                : 'Laatste HRV- en HMA-items worden automatisch toegevoegd'
              }
              description={lang === 'en'
                ? 'The most recent HRV and HMA analyses are selected by default. You can optionally select additional items below.'
                : 'De meest recente HRV- en HMA-analyses zijn standaard geselecteerd. U kunt hieronder optioneel extra items selecteren.'
              }
              type="success"
              showIcon
              style={{ marginBottom: 12 }}
            />

            {/* HRV Items */}
            {hrvLoading ? (
              <div style={{ textAlign: 'center', padding: '12px' }}>
                <Spin size="small" />
                <span style={{ marginLeft: 8 }}>
                  {lang === 'en' ? 'Loading HRV items...' : 'HRV-items laden...'}
                </span>
              </div>
            ) : hrvItems && hrvItems.length > 0 ? (
              <div style={{ marginBottom: 16 }}>
                <h5 style={{ marginBottom: 8 }}>
                  {lang === 'en' ? 'HRV (Heart Rate Variability) - Optional Additional Items' : 'HRV (Hartfrequentievariabiliteit) - Optionele Extra Items'}
                </h5>
                <Checkbox.Group
                  value={selectedHRVIds}
                  onChange={handleHRVSelection}
                  style={{ width: '100%' }}
                >
                  <Space direction="vertical" style={{ width: '100%' }}>
                    {hrvItems.map((item: any) => (
                      <div 
                        key={item.id}
                        style={{ 
                          padding: '12px', 
                          border: '1px solid #d9d9d9', 
                          borderRadius: '4px',
                          backgroundColor: selectedHRVIds.includes(item.id) ? '#f0f5ff' : '#fff'
                        }}
                      >
                        <Checkbox value={item.id}>
                          <div>
                            <strong>HRV Analysis - Report #{item.reportNumber || item.id}</strong>
                            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                              {item.dateOfAnalysis && `Date: ${new Date(item.dateOfAnalysis).toLocaleDateString()}`}
                              {item.durationOfAnalysis && ` | Duration: ${item.durationOfAnalysis}`}
                            </div>
                          </div>
                        </Checkbox>
                      </div>
                    ))}
                  </Space>
                </Checkbox.Group>

              </div>
            ) : (
              <div style={{ marginBottom: 16 }}>
                <Alert
                  message={lang === 'en' ? 'No HRV data available' : 'Geen HRV-gegevens beschikbaar'}
                  description={lang === 'en' 
                    ? 'No HRV analysis data found for this client.' 
                    : 'Geen HRV-analysegegevens gevonden voor deze cliënt.'
                  }
                  type="info"
                  showIcon
                />
              </div>
            )}

            {/* HMA Items */}
            {hmaLoading ? (
              <div style={{ textAlign: 'center', padding: '12px' }}>
                <Spin size="small" />
                <span style={{ marginLeft: 8 }}>
                  {lang === 'en' ? 'Loading HMA items...' : 'HMA-items laden...'}
                </span>
              </div>
            ) : hmaItems && hmaItems.length > 0 ? (
              <div style={{ marginBottom: 16 }}>
                <h5 style={{ marginBottom: 8 }}>
                  {lang === 'en' ? 'HMA (Hair Mineral Analysis) - Optional Additional Items' : 'HMA (Haarmineralanalyse) - Optionele Extra Items'}
                </h5>
                <Checkbox.Group
                  value={selectedHMAIds}
                  onChange={handleHMASelection}
                  style={{ width: '100%' }}
                >
                  <Space direction="vertical" style={{ width: '100%' }}>
                    {hmaItems.map((item: any) => (
                      <div 
                        key={item.id}
                        style={{ 
                          padding: '12px', 
                          border: '1px solid #d9d9d9', 
                          borderRadius: '4px',
                          backgroundColor: selectedHMAIds.includes(item.id) ? '#f0f5ff' : '#fff'
                        }}
                      >
                        <Checkbox value={item.id}>
                          <div>
                            <strong>HMA Analysis - Report #{item.reportNumber || item.id}</strong>
                            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                              {item.dateOfAnalysis && `Date: ${new Date(item.dateOfAnalysis).toLocaleDateString()}`}
                              {item.hairType && ` | Hair Type: ${item.hairType}`}
                            </div>
                          </div>
                        </Checkbox>
                      </div>
                    ))}
                  </Space>
                </Checkbox.Group>

              </div>
            ) : (
              <Alert
                message={lang === 'en' ? 'No HMA data available' : 'Geen HMA-gegevens beschikbaar'}
                description={lang === 'en' 
                  ? 'No HMA analysis data found for this client.' 
                  : 'Geen HMA-analysegegevens gevonden voor deze cliënt.'
                }
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}
          </div>
        )}

        {/* Upload Documents Section */}
        <div style={{ marginBottom: 24 }}>
          <h4 style={{ marginBottom: 12, fontWeight: 'bold' }}>
            {lang === 'en' ? 'Upload Documents' : 'Documenten Uploaden'}
          </h4>
          <Alert
            message={lang === 'en' 
              ? 'Upload PDF or Word documents to include in the AI analysis' 
              : 'Upload PDF- of Word-documenten om op te nemen in de AI-analyse'
            }
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
          />
          <Upload.Dragger
            multiple
            accept=".pdf,.doc,.docx"
            fileList={uploadedFiles}
            onChange={(info) => setUploadedFiles(info.fileList)}
            beforeUpload={() => false}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              {lang === 'en'
                ? 'Click or drag files here to upload'
                : 'Klik of sleep bestanden naar dit gebied om te uploaden'}
            </p>
            <p className="ant-upload-hint">
              {lang === 'en'
                ? 'Supports PDF, DOC, and DOCX files.'
                : 'Ondersteunt PDF-, DOC- en DOCX-bestanden.'}
            </p>
          </Upload.Dragger>
        </div>

        <div>
          <h4 style={{ marginBottom: 12, fontWeight: 'bold' }}>
            {lang === 'en' ? 'Analysis Type' : 'Analysetype'}
          </h4>
          <Alert
            message={lang === 'en' 
              ? 'Choose the type of analysis prompt to use' 
              : 'Kies het type analyseprompt om te gebruiken'
            }
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
          />
          <Radio.Group
            value={selectedPrompt}
            onChange={(e) => handlePromptSelection(e.target.value)}
            style={{ width: '100%' }}
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              <div 
                style={{ 
                  padding: '12px', 
                  border: '1px solid #d9d9d9', 
                  borderRadius: '4px',
                  backgroundColor: selectedPrompt === 'Prompt_1' ? '#f0f5ff' : '#fff'
                }}
              >
                <Radio value="Prompt_1">
                  <div>
                    <strong>{lang === 'en' ? 'Prompt 1 - Standard Analysis' : 'Prompt 1 - Standaard analyse'}</strong>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                      {lang === 'en' 
                        ? 'Comprehensive health analysis with primary and secondary dynamics' 
                        : 'Uitgebreide gezondheidsanalyse met primaire en secundaire dynamieken'
                      }
                    </div>
                  </div>
                </Radio>
              </div>
              <div 
                style={{ 
                  padding: '12px', 
                  border: '1px solid #d9d9d9', 
                  borderRadius: '4px',
                  backgroundColor: selectedPrompt === 'Prompt_2' ? '#f0f5ff' : '#fff'
                }}
              >
                <Radio value="Prompt_2">
                  <div>
                    <strong>{lang === 'en' ? 'Prompt 2 - Advanced Analysis' : 'Prompt 2 - Geavanceerde analyse'}</strong>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                      {lang === 'en' 
                        ? 'In-depth analysis with extended recommendations and insights' 
                        : 'Diepgaande analyse met uitgebreide aanbevelingen en inzichten'
                      }
                    </div>
                  </div>
                </Radio>
              </div>
              <div 
                style={{ 
                  padding: '12px', 
                  border: '1px solid #d9d9d9', 
                  borderRadius: '4px',
                  backgroundColor: selectedPrompt === 'Prompt_3' ? '#f0f5ff' : '#fff'
                }}
              >
                <Radio value="Prompt_3">
                  <div>
                    <strong>{lang === 'en' ? 'Prompt 3 - Transcription Analysis' : 'Prompt 3 - Transcriptie analyse'}</strong>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                      {lang === 'en' 
                        ? 'Enhanced analysis using Prompt 2 results with consultation transcription' 
                        : 'Verbeterde analyse met Prompt 2 resultaten en consulttranscriptie'
                      }
                    </div>
                  </div>
                </Radio>
              </div>
            </Space>
          </Radio.Group>
        </div>
      </Modal>
    </Card>
  );
};

export default NewAIQuestionnaireProfessional;